import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import { loginAgent, registerAgent } from '../utils/api';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AgentLogin = ({ defaultTeam = null, defaultTab = null }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(defaultTab !== 'register');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [team] = useState(defaultTeam);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});
  const [registerErrors, setRegisterErrors] = useState({});
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [registerData, setRegisterData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreedToTerms: false
  });

  // Restore remembered email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      const init = async () => setLoginData(prev => ({ ...prev, email: savedEmail, rememberMe: true }));
      init();
    }
  }, []);

  // Blur validators
  const validateLoginField = (field, value) => {
    const errs = { ...loginErrors };
    if (field === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errs.email = 'Please enter a valid email address';
    else if (field === 'password' && !value) errs.password = 'Password is required';
    else delete errs[field];
    setLoginErrors(errs);
  };

  const validateRegField = (field, value) => {
    const errs = { ...registerErrors };
    if (field === 'fullName' && value.length < 2) errs.fullName = 'Name must be at least 2 characters';
    else if (field === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errs.email = 'Please enter a valid email address';
    else if (field === 'phone' && value && value.replace(/\D/g, '').length < 10) errs.phone = 'Please enter a valid phone number (10+ digits)';
    else if (field === 'password' && value.length < 8) errs.password = 'Password must be at least 8 characters';
    else if (field === 'confirmPassword' && value !== registerData.password) errs.confirmPassword = 'Passwords do not match';
    else delete errs[field];
    setRegisterErrors(errs);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await loginAgent({
        email: loginData.email,
        password: loginData.password,
        rememberMe: loginData.rememberMe
      });

      if (result.success) {
        // Handle remember me
        if (loginData.rememberMe) {
          localStorage.setItem('rememberedEmail', loginData.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }

        localStorage.setItem('agentToken', result.access_token);
        localStorage.setItem('agentInfo', JSON.stringify(result.agent));
        
        toast.success(`Welcome back, ${result.agent.fullName}!`);
        const role = result.agent.role;
        navigate(role === 'admin' || role === 'super_admin' ? '/portal' : '/agent/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      const detail = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      if (detail.includes('pending')) {
        toast.error('Your account is pending admin approval. Please wait for approval.');
      } else if (detail.includes('rejected')) {
        toast.error('Your account has been rejected. Please contact the administrator.');
      } else {
        toast.error(detail);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!registerData.agreedToTerms) {
      toast.error('You must agree to the Terms of Service to create an account.');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await registerAgent({
        fullName: registerData.fullName,
        email: registerData.email,
        phone: registerData.phone,
        password: registerData.password,
        team: team || null
      });

      if (result.success) {
        toast.success(result.message || 'Registration submitted! Pending admin approval.');
        setIsLogin(true);
        setRegisterData({ fullName: '', email: '', phone: '', password: '', confirmPassword: '', agreedToTerms: false });
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Google OAuth handler
  const handleGoogleLogin = () => {
    const currentUrl = window.location.origin + '/agent/login';
    window.location.href = `https://demobackend.emergentagent.com/auth/v1/env/oauth/google?redirect_uri=${encodeURIComponent(currentUrl)}`;
  };

  // Handle Google OAuth callback
  const processGoogleSession = useCallback(async (sessionId) => {
    setIsSubmitting(true);
    try {
      const resp = await axios.post(`${API}/api/auth/google/session`, { session_id: sessionId });
      const result = resp.data;
      
      if (result.success) {
        localStorage.setItem('agentToken', result.access_token);
        localStorage.setItem('agentInfo', JSON.stringify(result.agent));
        toast.success(`Welcome, ${result.agent.fullName}!`);
        const role = result.agent.role;
        navigate(role === 'admin' || role === 'super_admin' ? '/portal' : '/agent/dashboard');
      } else if (result.status === 'pending') {
        toast.info(result.message || 'Registration submitted! Pending admin approval.');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Google sign-in failed';
      if (detail.includes('pending')) {
        toast.error('Your account is pending admin approval.');
      } else if (detail.includes('rejected')) {
        toast.error('Your account has been rejected.');
      } else {
        toast.error(detail);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [navigate]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      const init = async () => {
        await processGoogleSession(sessionId);
        window.history.replaceState({}, '', '/agent/login');
      };
      init();
    }
  }, [searchParams, processGoogleSession]);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-brand-blue mb-2">Agent Portal</h1>
            <p className="text-gray-600">Access your dashboard and tools</p>
            {team && (
              <div className="mt-3 inline-block px-4 py-2 rounded-full bg-brand-blue/10 text-brand-blue font-semibold text-sm" data-testid="team-badge">
                Team: {team === 'DOT' ? 'Disciples of Truth (DOT)' : 'Hawkins Investment Group (HIG)'}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Toggle Buttons */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-2 px-4 rounded-md font-semibold transition ${
                  isLogin
                    ? 'bg-brand-blue text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Login
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-2 px-4 rounded-md font-semibold transition ${
                  !isLogin
                    ? 'bg-brand-blue text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Register
              </button>
            </div>

            {/* Login Form */}
            {isLogin ? (
              <form onSubmit={handleLogin}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      id="login-email"
                      type="email"
                      required
                      value={loginData.email}
                      onChange={(e) => { setLoginData({ ...loginData, email: e.target.value }); if (loginErrors.email) setLoginErrors({ ...loginErrors, email: undefined }); }}
                      onBlur={(e) => validateLoginField('email', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${loginErrors.email ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="agent@example.com"
                      data-testid="login-email"
                      aria-describedby={loginErrors.email ? 'login-email-error' : undefined}
                    />
                    {loginErrors.email && <p id="login-email-error" className="text-xs text-red-500 mt-1" role="alert">{loginErrors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        id="login-password"
                        type={showLoginPassword ? 'text' : 'password'}
                        required
                        value={loginData.password}
                        onChange={(e) => { setLoginData({ ...loginData, password: e.target.value }); if (loginErrors.password) setLoginErrors({ ...loginErrors, password: undefined }); }}
                        onBlur={(e) => validateLoginField('password', e.target.value)}
                        className={`w-full px-4 py-3 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${loginErrors.password ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="Enter your password"
                        data-testid="login-password"
                        aria-describedby={loginErrors.password ? 'login-password-error' : undefined}
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                        data-testid="login-toggle-password"
                        tabIndex={-1}
                      >
                        {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {loginErrors.password && <p id="login-password-error" className="text-xs text-red-500 mt-1" role="alert">{loginErrors.password}</p>}
                  </div>

                  {/* Remember Me & Forgot Password row */}
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="remember-me-label">
                      <input
                        type="checkbox"
                        checked={loginData.rememberMe}
                        onChange={(e) => setLoginData({ ...loginData, rememberMe: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                        data-testid="remember-me-checkbox"
                      />
                      <span className="text-sm text-gray-600">Remember me</span>
                    </label>
                    <Link
                      to="/agent/forgot-password"
                      className="text-sm text-brand-blue font-medium hover:text-brand-orange transition"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-brand-blue text-white px-6 py-3 rounded-md hover:bg-brand-orange transition font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="login-submit-btn"
                  >
                    <LogIn size={20} />
                    {isSubmitting ? 'Logging in...' : 'Login'}
                  </button>

                  {/* Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div>
                    <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">or</span></div>
                  </div>

                  {/* Google OAuth - REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    className="w-full bg-white border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-50 transition font-semibold flex items-center justify-center gap-3"
                    data-testid="google-login-btn"
                  >
                    <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/></svg>
                    Continue with Google
                  </button>
                </div>
              </form>
            ) : (
              /* Register Form */
              <form onSubmit={handleRegister}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="reg-name" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      id="reg-name"
                      type="text"
                      required
                      value={registerData.fullName}
                      onChange={(e) => { setRegisterData({ ...registerData, fullName: e.target.value }); if (registerErrors.fullName) setRegisterErrors({ ...registerErrors, fullName: undefined }); }}
                      onBlur={(e) => validateRegField('fullName', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${registerErrors.fullName ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="John Doe"
                      data-testid="register-fullname"
                      aria-describedby={registerErrors.fullName ? 'reg-name-error' : undefined}
                    />
                    {registerErrors.fullName && <p id="reg-name-error" className="text-xs text-red-500 mt-1" role="alert">{registerErrors.fullName}</p>}
                  </div>

                  <div>
                    <label htmlFor="reg-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      id="reg-email"
                      type="email"
                      required
                      value={registerData.email}
                      onChange={(e) => { setRegisterData({ ...registerData, email: e.target.value }); if (registerErrors.email) setRegisterErrors({ ...registerErrors, email: undefined }); }}
                      onBlur={(e) => validateRegField('email', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${registerErrors.email ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="agent@example.com"
                      data-testid="register-email"
                      aria-describedby={registerErrors.email ? 'reg-email-error' : undefined}
                    />
                    {registerErrors.email && <p id="reg-email-error" className="text-xs text-red-500 mt-1" role="alert">{registerErrors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="reg-phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      id="reg-phone"
                      type="tel"
                      required
                      value={registerData.phone}
                      onChange={(e) => { setRegisterData({ ...registerData, phone: e.target.value }); if (registerErrors.phone) setRegisterErrors({ ...registerErrors, phone: undefined }); }}
                      onBlur={(e) => validateRegField('phone', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${registerErrors.phone ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="(912) 376-9006"
                      data-testid="register-phone"
                      aria-describedby={registerErrors.phone ? 'reg-phone-error' : undefined}
                    />
                    {registerErrors.phone && <p id="reg-phone-error" className="text-xs text-red-500 mt-1" role="alert">{registerErrors.phone}</p>}
                  </div>

                  <div>
                    <label htmlFor="reg-password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        id="reg-password"
                        type={showRegPassword ? 'text' : 'password'}
                        required
                        value={registerData.password}
                        onChange={(e) => { setRegisterData({ ...registerData, password: e.target.value }); if (registerErrors.password) setRegisterErrors({ ...registerErrors, password: undefined }); }}
                        onBlur={(e) => validateRegField('password', e.target.value)}
                        className={`w-full px-4 py-3 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${registerErrors.password ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="Minimum 8 characters"
                        data-testid="register-password"
                        aria-describedby={registerErrors.password ? 'reg-pw-error' : undefined}
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegPassword(!showRegPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                        data-testid="register-toggle-password"
                        tabIndex={-1}
                      >
                        {showRegPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {/* Password Strength Indicator */}
                    {registerData.password && (() => {
                      const pw = registerData.password;
                      let score = 0;
                      if (pw.length >= 8) score++;
                      if (pw.length >= 10) score++;
                      if (/[A-Z]/.test(pw)) score++;
                      if (/[0-9]/.test(pw)) score++;
                      if (/[^A-Za-z0-9]/.test(pw)) score++;
                      const level = score <= 1 ? 'Weak' : score <= 3 ? 'Medium' : 'Strong';
                      const color = score <= 1 ? 'bg-red-500' : score <= 3 ? 'bg-yellow-500' : 'bg-green-500';
                      const textColor = score <= 1 ? 'text-red-600' : score <= 3 ? 'text-yellow-600' : 'text-green-600';
                      const width = score <= 1 ? 'w-1/4' : score <= 3 ? 'w-2/4' : 'w-full';
                      return (
                        <div className="mt-2" data-testid="password-strength">
                          <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full ${color} ${width} rounded-full transition-all duration-300`}></div>
                          </div>
                          <p className={`text-xs mt-1 font-medium ${textColor}`}>Password strength: {level}</p>
                        </div>
                      );
                    })()}
                    {registerErrors.password && <p id="reg-pw-error" className="text-xs text-red-500 mt-1" role="alert">{registerErrors.password}</p>}
                  </div>

                  <div>
                    <label htmlFor="reg-confirm" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <input
                        id="reg-confirm"
                        type={showRegConfirm ? 'text' : 'password'}
                        required
                        value={registerData.confirmPassword}
                        onChange={(e) => { setRegisterData({ ...registerData, confirmPassword: e.target.value }); if (registerErrors.confirmPassword) setRegisterErrors({ ...registerErrors, confirmPassword: undefined }); }}
                        onBlur={(e) => validateRegField('confirmPassword', e.target.value)}
                        className={`w-full px-4 py-3 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${registerErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="Re-enter password"
                        data-testid="register-confirm-password"
                        aria-describedby={registerErrors.confirmPassword ? 'reg-confirm-error' : undefined}
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegConfirm(!showRegConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                        data-testid="register-toggle-confirm"
                        tabIndex={-1}
                      >
                        {showRegConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {registerErrors.confirmPassword && <p id="reg-confirm-error" className="text-xs text-red-500 mt-1" role="alert">{registerErrors.confirmPassword}</p>}
                  </div>

                  {/* Terms of Service Checkbox */}
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={registerData.agreedToTerms}
                      onChange={(e) => setRegisterData({ ...registerData, agreedToTerms: e.target.checked })}
                      className="w-4 h-4 mt-0.5 rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                      data-testid="terms-checkbox"
                    />
                    <span className="text-sm text-gray-600">
                      I agree to the{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-blue font-medium hover:text-brand-orange transition underline" data-testid="terms-link">
                        Terms of Service
                      </a>
                      {' '}and{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-blue font-medium hover:text-brand-orange transition underline" data-testid="privacy-link">
                        Privacy Policy
                      </a>
                    </span>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || !registerData.agreedToTerms}
                    className="w-full bg-brand-orange text-white px-6 py-3 rounded-md hover:bg-brand-orange-light transition font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="register-submit-btn"
                  >
                    <UserPlus size={20} />
                    {isSubmitting ? 'Creating Account...' : 'Create Account'}
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="text-center text-sm text-gray-600 mt-6">
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-brand-blue font-semibold hover:text-brand-orange"
            >
              {isLogin ? 'Register here' : 'Login here'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgentLogin;
